import {geohashForLocation} from 'geofire-common';

import {DisplayField} from '@/lib/classes/InfoWindowContent';
import {addDocument, currentUser, updateDocument, countFromCollection, GeoPoint, createDocRef, getDocument, runInTransaction} from '../FirestoreService';
import {createUpdateQueue} from '../FirestoreService/updateQueue';
import {MapService, Markers} from '../MapService';
import {getStore} from '../store';
import {currentUserAction, type UserAction} from '../currentUserAction';
import {flushOpenReviewEdits} from '../reviewEdits';
import {getCollection} from '@/lib/filters/filters';
import {createMarkerEventHandlers, getAggregatedFields} from '@/lib/classes/InfoWindowContent/helpers/competitive';
import {FileToProcess, LightOMItem, OMItem, OMReviewItem, UploadResponse} from './types';
import {toLightOMItem} from './light';
import {collectionNameByFirestoreCollection} from '@/internal/store/collections';
import {wrapError} from '@northpoint-development/labs-common-core';
import {callProcedure} from '../BigQueryService';
import {createHttpClient} from '../HttpService';
import {backend} from '../BackendService/client';
import {FlagService, FLAGS} from '../FlagService';
import type {AwaitDoc} from '../FilterService';
import {
  BaseScenarioInput,
  PortfolioLinkedBuildingInput,
  buildBaseScenario,
} from '@/lib/underwriting/adapters/scenario/baseScenario';
import {writeScenario} from '@/lib/underwriting/adapters/scenario/scenario';


/** An awaitDoc for a non-filterable collection would 400 the entire refresh batch. */
const AWAITABLE_FILTER_COLLECTIONS = new Set(['competitiveBuildings', 'competitivePortfolios']);

/**
 * After an approval or deletion, re-runs backend filters so newly committed and link-rewritten
 * docs appear (or refresh their filter status) without a manual filter change. Fire-and-forget.
 */
const refreshBackendFiltersForDocs = (awaitDocs: AwaitDoc[]): void => {
  const byKey = new Map<string, AwaitDoc>();
  for (const doc of awaitDocs) {
    if (!AWAITABLE_FILTER_COLLECTIONS.has(doc.collection)) continue;
    const key = `${doc.collection}/${doc.id}`;
    const existing = byKey.get(key);
    if (!existing || doc.editedAtMillis > existing.editedAtMillis) {
      byKey.set(key, doc);
    }
  }
  if (!byKey.size) return;
  try {
    getStore().dispatch('filters/refreshBackendFilters', {awaitDocs: [...byKey.values()]});
  } catch (error) {
    console.error('Failed to dispatch backend filter refresh after OM approval/deletion:', error);
  }
};


const safeWriteBaseScenario = async (input: BaseScenarioInput): Promise<void> => {
  if (!FlagService?.getBooleanFlag(FLAGS.UNDERWRITING_V2_ENABLED)) return;
  try {
    await writeScenario(buildBaseScenario(input));
  } catch (error) {
    // Best-effort: log and continue. The dashboard read path rebuilds from the source doc
    // if the scenario is missing, so approval workflow must not fail on this write.
    console.error(wrapError(error, `Failed to write base scenario for ${input.kind} ${input.recordId}`));
  }
};

interface LinkedDoc {
  collection: string;
  id: string;
}
interface OmParent {
  id: string;
  location: {latitude: number; longitude: number};
  comps: {length: number} | null;
}
/**
 * Shared contract for committing a reviewed competitive record (OM or bulk) to the
 * real `beyondMaps-properties`/`-portfolios` collections and its marker layer. OM
 * and bulk each extend this with their own staging-collection + committed-id field
 * (see `BulkConfigItem` in BulkStagingService/config.ts).
 */
export interface CompetitiveCommitConfig {
  finalFirestoreCollection: string;
  finalFilterCollectionType: string;
  finalInfoWindowAction: string;
  /** Marker layer / content type for the in-review staging marker. */
  fullType: string;
  localCollection: string;
  markerIcon: string;
  type: string;
}
interface OmConfigItem extends CompetitiveCommitConfig {
  firestoreCollection: string;
  finalOmIdKey: 'createdCompetitiveBuildingId' | 'createdCompetitivePortfolioId';
  bigQuerySourceTable: string;
  bigQueryDestTable: string;
}
const omConfig: Record<string, OmConfigItem> = {
  'building': {
    firestoreCollection: 'beyondMaps-properties-om',
    finalFirestoreCollection: 'beyondMaps-properties',
    finalFilterCollectionType: 'competitiveBuilding',
    finalInfoWindowAction: 'createCompetitiveBuildingTab',
    finalOmIdKey: 'createdCompetitiveBuildingId',
    fullType: 'omCompetitiveBuilding',
    localCollection: 'omCompetitiveBuildings',
    markerIcon: 'Darker-Blue-Balloon',
    type: 'building',
    bigQuerySourceTable: 'propertiesom_export_raw_changelog',
    bigQueryDestTable: 'properties_export_raw_changelog',
  },
  'portfolio': {
    firestoreCollection: 'beyondMaps-portfolios-om',
    finalFirestoreCollection: 'beyondMaps-portfolios',
    finalFilterCollectionType: 'competitivePortfolio',
    finalInfoWindowAction: 'createCompetitivePortfolioTab',
    finalOmIdKey: 'createdCompetitivePortfolioId',
    fullType: 'omCompetitivePortfolio',
    localCollection: 'omCompetitivePortfolios',
    markerIcon: 'Darker-Blue-Portfolio',
    type: 'portfolio',
    bigQuerySourceTable: 'portfoliosom_export_raw_changelog',
    bigQueryDestTable: 'portfolios_export_raw_changelog',
  },
};


/**
 * Creates a Salesforce Acquisition record for an approved OM portfolio or building.
 * @param omCollection - The OM collection (e.g., 'beyondMaps-properties-om')
 * @param omDocId - The OM document ID
 * @returns The Salesforce record ID if successful, null otherwise.
 */
const createSalesforceRecord = async (omCollection: 'beyondMaps-properties-om' | 'beyondMaps-portfolios-om', omDocId: string): Promise<string | null> => {
  try {
    const client = createHttpClient('NORTHPOINT_PRODUCTION');
    const response = await client.post<{salesforceId: string}>('createSalesforceRecord', {
      collection: omCollection,
      docId: omDocId,
    });
    return response.salesforceId;
  } catch (error) {
    console.error(wrapError(error, `Failed to create Salesforce record for ${omCollection}/${omDocId}`));
    return null;
  }
};

/**
 * Checks if Salesforce record creation is enabled.
 */
const isSalesforceCreateEnabled = (): boolean => {
  const flagValue = FlagService.getBooleanFlag(FLAGS.ENABLE_SALESFORCE_PROJECT_CREATE);
  if (!flagValue) {
    console.warn(`Salesforce record creation is disabled via feature flag in environment.`);
    return false;
  }
  return true;
};

// Three times the slowest prod call seen (9 s); the function itself can run for 9 minutes.
const SALESFORCE_WAIT_MS = 30_000;

/** Safe to retry: the cloud function returns the record it already created for this OM id. */
const createSalesforceRecordIfEnabled = async (
  omCollection: 'beyondMaps-properties-om' | 'beyondMaps-portfolios-om',
  omDocId: string,
): Promise<{salesforceId: string | null; failed: boolean}> => {
  if (!isSalesforceCreateEnabled()) return {salesforceId: null, failed: false};
  let timer: ReturnType<typeof setTimeout> | undefined;
  const gaveUp = new Promise<null>((resolve) => {
    timer = setTimeout(() => {
      console.error(`Salesforce didn't answer within ${SALESFORCE_WAIT_MS / 1000} s for ${omCollection}/${omDocId}`);
      resolve(null);
    }, SALESFORCE_WAIT_MS);
  });
  const salesforceId = await Promise.race([createSalesforceRecord(omCollection, omDocId), gaveUp]);
  clearTimeout(timer);
  return {salesforceId, failed: !salesforceId};
};

/**
 * Uploads OM pdf file(s) by sending their GCS locations to the backend.
 * @param filesToProcess - An array of objects detailing the GCS location and original name of files to be processed.
 */
const uploadDocument = async (filesToProcess: FileToProcess[]) : Promise<UploadResponse> => {
  const idToken = await currentUser()?.getIdToken();
  const urls = {
    staging: 'https://us-central1-beyondmaps-dev.cloudfunctions.net/uploadDocument-staging',
    production: 'https://us-central1-northpoint-production.cloudfunctions.net/uploadDocument',
  };
  const env = (process.env.VUE_APP_GCP_ENV === 'production') ? 'production' : 'staging';
  const cloudFunctionUrl = urls[env];

  const response = await fetch(cloudFunctionUrl, {
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
    body: JSON.stringify({filesToProcess}),
  });

  const json = await response.json();
  if (response.status !== 200) {
    throw new Error(`Error uploading document: ${json.error}`);
  }

  return json as UploadResponse;
};

/** Deletes an upload's files and record: the rollback when its OM could not be created. */
const deleteOmUpload = async (hash: string): Promise<void> => {
  await backend.post('internal-public-api', {hash}, {path: 'api/om-uploads/delete'});
};

/**
 * Returns a list of OMs ready for review, grouped by portfolios and standalone buildings.
 * - Portfolios are included if their status is 'review' or any linked building is in 'review'.
 * - Each portfolio includes itself and its linked buildings as children.
 * - Standalone buildings (not linked to any portfolio) with status 'review' are included as individual items.
 * The result is a flat array of OMReviewItem objects, each representing either a portfolio (with children)
 * or a standalone building.
 * ex:
 * [
 *   { ...portfolio, type: 'portfolio', children: [
 *       { ...portfolio, type: 'portfolio', children: null }, // Portfolio itself
 *       { ...building, type: 'building', children: null }, // Linked building 1
 *       { ...building, type: 'building', children: null }  // Linked building 2
 *  ]},
 *  { ...building, type: 'building', children: null } // Standalone building in review
 * ]
 */
const getReadyForReviewOMs = () => {
  const result: OMReviewItem[] = [];

  // Keep track of buildings that are linked to a portfolio
  const linkedBuildingIds = new Set();

  getStore().state.omCompetitivePortfolios.forEach((portfolio: LightOMItem) => {
    const linkedBuildings = portfolio.linkedDocuments
      ? portfolio.linkedDocuments
          .filter((doc: {id: string; collection: string}) => doc.collection === 'beyondMaps-properties-om')
          .map(
            (doc: {id: string; collection: string}) => {
              const building = getStore().state.omCompetitiveBuildings.find(
                (b: LightOMItem) => b.id === doc.id,
              );
              if (!building || !building.id) return null;
              if (building.status !== 'review' && building.status !== 'approved') return null;
              linkedBuildingIds.add(building.id);
              return {...building, type: 'building', children: null};
            },
          )
          .filter(Boolean)
          .sort((a: LightOMItem, b: LightOMItem) => a.name.localeCompare(b.name))
      : [];

    // If the portfolio status is 'review' OR if any of its linked buildings are in 'review', include it
    const isPortfolioInReview = portfolio.status === 'review';
    if (!isPortfolioInReview && linkedBuildings.length === 0) return;

    const hasBuildingInReview = linkedBuildings.some(
      (building: LightOMItem) => building.status === 'review',
    );
    if (!isPortfolioInReview && !hasBuildingInReview) return;

    const children = [];
    if (portfolio.status !== 'deleted') {
      children.push({
        ...portfolio,
        type: 'portfolio',
        children: null,
      });
    }
    children.push(...linkedBuildings);

    result.push({
      ...portfolio,
      type: 'portfolio',
      children,
    });
  });

  // Buildings that are in 'review' status and not linked to any portfolio are standalone
  const buildingsReadyForReview = getStore().state.omCompetitiveBuildings.filter(
    (building: LightOMItem) => building.status === 'review' && building.id && !linkedBuildingIds.has(building.id),
  );

  result.push(...buildingsReadyForReview.map((building: LightOMItem) => ({
    ...building,
    type: 'building',
    children: null,
  })));

  result.sort((a, b) => {
    const bTimestamp = Number(b.generatedBy?.timestamp) || 0;
    const aTimestamp = Number(a.generatedBy?.timestamp) || 0;
    return bTimestamp - aTimestamp;
  });

  return result;
};

/**
 * Adds aggregated fields (Total Available and Leased Percentage) to a competitive building body.
 * These fields are calculated based on building size and comps data.
 * This adds them to the local filter collection for UI/export but does NOT persist to Firestore.
 * Note: Aggregated fields have allowEdit: false and are filtered out when saving to Firestore.
 */
const addAggregatedFieldsToBody = (
  body: {displayFields?: DisplayField[] | null} | null,
): void => {
  // Only calculate aggregated fields for buildings with displayFields
  if (!body || !body.displayFields) return;
  const displayFields = body.displayFields;

  const AGGREGATED_FIELD_LABELS = ['Total Available', 'Leased Percentage'];
  const AGGREGATED_FIELD_KEYS = ['totalAvailable', 'leasedPercentage'];

  // Remove any existing Total Available or Leased Percentage fields to avoid duplicates
  body.displayFields = displayFields.filter(
    (field: DisplayField) => !AGGREGATED_FIELD_LABELS.includes(field.label),
  );

  // Calculate the aggregated fields using the existing aggregator logic
  const aggregatedFields = getAggregatedFields(body, AGGREGATED_FIELD_KEYS);

  // Add aggregated DisplayField instances to displayFields
  if (aggregatedFields && Array.isArray(aggregatedFields)) {
    aggregatedFields.forEach((field) => {
      if (field && field.value != null) {
        body.displayFields!.push(new DisplayField({
          label: field.label,
          value: field.value,
          unit: field.unit,
          allowEdit: field.allowEdit,
          allowLabelEdit: field.allowLabelEdit ?? false,
          allowDelete: field.allowDelete ?? false,
          valueOverridden: field.valueOverridden ?? false,
          uuid: field.uuid,
          pickerOptions: field.pickerOptions ?? [],
          displayFields: field.displayFields ?? [],
        }));
      }
    });
  }
};

/**
 * Builds the committed competitive doc body for the final collections. Pure — no I/O.
 * Shared by OM and bulk approval so both produce an identical shape.
 */
const buildCommittedCompetitiveBody = (params: {
  id: string;
  name: string;
  location: {latitude: number; longitude: number};
  displayFields: DisplayField[] | null | undefined;
  comps?: OMItem['comps'];
  underwritingData?: OMItem['underwritingData'];
  linkedDocuments: LinkedDoc[];
  generatedByOm: boolean;
  whoAndNow: UserAction;
  linkedSalesforceId?: string | null;
  bulkBatchId?: string | null;
}) => ({
  bulkBatchId: params.bulkBatchId ?? null,
  comps: params.comps ?? null,
  createdOn: params.whoAndNow,
  deleted: null,
  displayFields: params.displayFields ?? null,
  editedOn: params.whoAndNow,
  geohash: geohashForLocation([params.location.latitude, params.location.longitude]),
  generatedByOm: params.generatedByOm,
  id: params.id,
  linkedDocuments: params.linkedDocuments,
  linkedSalesforceId: params.linkedSalesforceId ?? null,
  location: new GeoPoint(params.location.latitude, params.location.longitude),
  name: params.name,
  underwritingData: params.underwritingData ?? {},
});

const buildCommittedRecord = (om: OMItem, id: string, newLinkedDocuments: LinkedDoc[], whoAndNow: UserAction) => {
  const linkedDocuments = newLinkedDocuments.concat(om.linkedDocuments ?? [])
    .filter((doc, index, self) => {
      const isFirstOccurence = self.findIndex((d) => d.id === doc.id) === index;
      const isLinkedOm = doc.collection.endsWith('-om');
      return isFirstOccurence && !isLinkedOm;
    });

  om.comps?.forEach((comp) => {
    if (comp.displayFields?.some((field) => field.citation)) {
      comp.generatedByOm = true;
    }
  });

  return buildCommittedCompetitiveBody({
    id,
    name: om.name,
    location: om.location!,
    displayFields: om.displayFields,
    comps: om.comps,
    underwritingData: om.underwritingData,
    linkedDocuments,
    generatedByOm: om.displayFields?.some((field) => field.citation) || false,
    whoAndNow,
    linkedSalesforceId: om.linkedSalesforceId ?? null,
  });
};

/** Marker payload for a committed competitive record (flat/dotless icon); shared by OM + bulk approval. */
const buildCommittedMarkerData = (
  cfg: CompetitiveCommitConfig,
  record: {id: string; name: string; location?: {latitude: number; longitude: number} | null},
) => ({
  clickable: true,
  collection: `${cfg.finalFilterCollectionType}s`,
  draggable: false,
  events: createMarkerEventHandlers(
    `${cfg.finalFilterCollectionType}s`,
    cfg.finalInfoWindowAction,
    cfg.finalFirestoreCollection,
    record.id,
  ),
  icon: cfg.markerIcon,
  id: record.id,
  name: record.name,
  type: cfg.finalFilterCollectionType,
  latLng: {lat: record.location?.latitude, lng: record.location?.longitude},
  visible: true,
});

const createMarkerData = (om: OMItem, id: string) => {
  try {
    return buildCommittedMarkerData(omConfig[om.type!], {id, name: om.name, location: om.location});
  } catch (error) {
    throw wrapError(error, 'Unable to create marker data');
  }
};

const formatOmComps = (om: Pick<OMItem, 'comps'>, parent: OmParent) => {
  try {
    const circlesByParent: Map<string, Array<google.maps.LatLngLiteral>> = new Map();
    return om.comps?.map((comp) => {
      // comp markers
      if (!circlesByParent.has(parent.id)) {
      // create circle of points for plotting comps around building
        circlesByParent.set(parent.id, MapService.createCircle(
          parent.location.latitude,
          parent.location.longitude,
          parent.comps!.length,
        ));
      }
      const point = circlesByParent.get(parent.id)!.pop();
      const type = comp.type ?? comp.group ?? 'Lease';
      const icon = `${getStore().getters['collections/colorFromCompGroup'](type)}-Balloon`;
      const clickEvent = {
        event: 'click',
        callback: () => {
          const subject = getCollection(`competitiveBuildingComps`).getItem(comp.id);
          getStore().dispatch('infowindow/createCompTab',
            {subject: subject},
            {root: true},
          );
        },
      };
      return {
        ...comp,
        parent,
        markerData: {
          type: 'comp',
          id: comp.id,
          name: comp.name,
          latLng: point,
          icon: icon,
          events: [clickEvent],
        },
      };
    }) ?? null;
  } catch (error) {
    throw wrapError(error, 'Unable to format OM comps');
  }
};

/**
 * Circular walk from `startIndex`, returning the next unresolved sibling or null when all are
 * resolved. Shared by OM and bulk to drive "resolve one → open the next item to review".
 */
const nextUnresolvedSibling = <T>(
  ordered: T[],
  startIndex: number,
  isResolved: (record: T) => boolean,
): T | null => {
  if (startIndex < 0) return null;
  for (let offset = 1; offset < ordered.length; offset++) {
    const candidate = ordered[(startIndex + offset) % ordered.length];
    if (!isResolved(candidate)) return candidate;
  }
  return null;
};

interface BatchMember {
  light: LightOMItem;
  config: OmConfigItem;
  committedId: string;
}

type BatchResolution =
  | {kind: 'approve'; whoAndNow: UserAction; linkedSalesforceId: string | null}
  | {kind: 'delete'; whoAndNow: UserAction}
  // The merge already approved it, into the existing record it names
  | {kind: 'merged'; whoAndNow: UserAction};

interface LinkEdit {
  collection: string;
  id: string;
  linkedDocuments: LinkedDoc[];
}

interface CommittedMember {
  om: OMItem;
  config: OmConfigItem;
  writeable: ReturnType<typeof buildCommittedCompetitiveBody>;
}

type BatchOutcome =
  | {kind: 'alreadyResolved'; status: string}
  | {kind: 'nextForReview'; next: LightOMItem; linkEdits: LinkEdit[]}
  | {
    kind: 'committed';
    committed: CommittedMember[];
    linkEdits: LinkEdit[];
    survivorIds: string[];
    releases: SurvivorRelease[];
  };

/** Whether a window opened: the next member's review, or a committed record. */
interface SiblingOutcome {
  awaitDocs: AwaitDoc[];
  opened: boolean;
}

const docPath = (collection: string, id: string) => `${collection}/${id}`;

const standaloneMember = (config: OmConfigItem, omId: string, om: OMItem): BatchMember => ({
  light: getStore().state[config.localCollection].find((o: LightOMItem) => o.id === omId)
    ?? toLightOMItem({...om, id: omId}),
  config,
  committedId: createDocRef(config.finalFirestoreCollection).id,
});

const batchMembers = (omBatchId: string): BatchMember[] => {
  const portfolios = getStore().state.omCompetitivePortfolios
    .filter((o: LightOMItem) => o.omBatchId === omBatchId);
  const buildings = getStore().state.omCompetitiveBuildings
    .filter((o: LightOMItem) => o.omBatchId === omBatchId)
    .sort((a: LightOMItem, b: LightOMItem) => a.name.localeCompare(b.name));
  return [
    ...portfolios.map((light: LightOMItem) => ({light, config: omConfig.portfolio})),
    ...buildings.map((light: LightOMItem) => ({light, config: omConfig.building})),
  ].map((member) => ({...member, committedId: createDocRef(member.config.finalFirestoreCollection).id}));
};

/** `survivors` stand in the portfolio scenario for the members merged into them. */
const baseScenarioInputs = (
  committed: CommittedMember[],
  survivors: PortfolioLinkedBuildingInput[],
): BaseScenarioInput[] => {
  const recordInput = ({writeable}: CommittedMember) => ({
    recordId: writeable.id,
    recordName: writeable.name,
    recordLocation: writeable.location,
    underwritingData: writeable.underwritingData,
    comps: writeable.comps ?? [],
    displayFields: writeable.displayFields ?? [],
    whoAndNow: writeable.createdOn,
  });
  const buildings = committed.filter((member) => member.config.type === 'building');
  const portfolios = committed.filter((member) => member.config.type === 'portfolio');
  return portfolios.length
    ? portfolios.map((portfolio) => ({
        kind: 'portfolio' as const,
        ...recordInput(portfolio),
        buildings: [...survivors, ...buildings.map(({writeable}): PortfolioLinkedBuildingInput => ({
          id: writeable.id,
          name: writeable.name,
          location: writeable.location,
          underwritingData: writeable.underwritingData,
          comps: writeable.comps ?? [],
          displayFields: writeable.displayFields ?? [],
        }))],
      }))
    : buildings.map((building) => ({kind: 'building' as const, ...recordInput(building)}));
};

interface LinkEditPlan {
  source: OMItem;
  edit: (links: LinkedDoc[]) => LinkedDoc[];
}

/** Call it before the transaction's first write: it reads the linked records outside the Batch. */
const planLinkEdits = async (
  transaction: {get: (collection: string, id: string) => Promise<{linkedDocuments?: LinkedDoc[] | null} | null>},
  oms: Map<string, OMItem>,
  plans: LinkEditPlan[],
): Promise<LinkEdit[]> => {
  const targets = new Map<string, LinkedDoc>();
  for (const {source} of plans) {
    for (const target of source.linkedDocuments ?? []) {
      targets.set(docPath(target.collection, target.id), target);
    }
  }
  const outside = [...targets].filter(([path]) => !oms.has(path));
  const read = await Promise.all(outside.map(([, target]) => transaction.get(target.collection, target.id)));
  const outsideDocs = new Map(outside.map(([path], i) => [path, read[i]]));

  // Edits replace these arrays rather than mutate them: committed records need the unedited links.
  const links = new Map<string, LinkEdit>();
  for (const [path, {collection, id}] of targets) {
    const doc = oms.get(path) ?? outsideDocs.get(path);
    if (!doc) {
      console.warn(`Could not find linked document ${path}, skipping...`);
      continue;
    }
    links.set(path, {collection, id, linkedDocuments: doc.linkedDocuments ?? []});
  }
  const edited = new Set<string>();
  for (const {source, edit} of plans) {
    for (const target of source.linkedDocuments ?? []) {
      const path = docPath(target.collection, target.id);
      const entry = links.get(path);
      if (!entry || !entry.linkedDocuments.some((link) => link.id === source.id)) continue;
      entry.linkedDocuments = edit(entry.linkedDocuments);
      edited.add(path);
    }
  }
  return [...edited].map((path) => links.get(path)!);
};

interface SurvivorRelease {
  id: string;
  update: {linkedDocuments?: LinkedDoc[]; heldFor?: null};
}

/**
 * Once a batch commits, each existing record one of its members was merged into joins the batch's committed
 * portfolios and, no longer held for the batch, returns to the map and search. Call it before the transaction's first
 * write: it reads those records.
 */
const planSurvivorReleases = async (
  transaction: {
    get: (collection: string, id: string) => Promise<{
      linkedDocuments?: LinkedDoc[] | null;
      heldFor?: LinkedDoc | null;
    } | null>;
  },
  survivorIds: string[],
  portfolioLinks: LinkedDoc[],
): Promise<SurvivorRelease[]> => {
  const survivors = await Promise.all(survivorIds.map((id) => transaction.get('beyondMaps-properties', id)));
  return survivorIds.flatMap((id, i): SurvivorRelease[] => {
    const survivor = survivors[i];
    if (!survivor) return [];
    const links = survivor.linkedDocuments ?? [];
    const isOwn = (link: LinkedDoc) => links.some((own) => own.collection === link.collection && own.id === link.id);
    const added = portfolioLinks.filter((link) => !isOwn(link));
    const held = survivor.heldFor != null;
    if (!added.length && !held) return [];
    return [{
      id,
      update: {
        ...(added.length ? {linkedDocuments: [...links, ...added]} : {}),
        ...(held ? {heldFor: null} : {}),
      },
    }];
  });
};

const showReleasedSurvivors = (releases: SurvivorRelease[], whoAndNow: UserAction): AwaitDoc[] =>
  releases.map(({id, update}) => {
    const item = getCollection('competitiveBuildings')?.getItem(id);
    if (item) {
      getCollection('competitiveBuildings').addItems({...item, ...update, editedOn: whoAndNow});
    }
    return {collection: 'competitiveBuildings', id, editedAtMillis: whoAndNow.timestamp.getTime()};
  });

/** The existing record a batch member was merged into, as the portfolio scenario's snapshot of that member. */
const survivorSnapshot = async (id: string): Promise<PortfolioLinkedBuildingInput | null> => {
  const doc = await getDocument('beyondMaps-properties', id) as Partial<OMItem> & {id?: string} | undefined;
  if (!doc) return null;
  return {
    id,
    name: doc.name ?? '',
    location: doc.location ?? null,
    underwritingData: doc.underwritingData ?? {},
    comps: (doc.comps ?? []) as PortfolioLinkedBuildingInput['comps'],
    displayFields: (doc.displayFields ?? []) as PortfolioLinkedBuildingInput['displayFields'],
  };
};

/**
 * A verdict that Resolves the Batch Commits it in the same transaction, so a reload leaves nothing written or all of
 * it. Members are re-read inside it, so two reviewers resolving the last two items at once still Commit once.
 */
const resolveOmItem = async (
  omId: string,
  members: BatchMember[],
  resolution: BatchResolution,
): Promise<SiblingOutcome | null> => {
  const ownIndex = members.findIndex((member) => member.light.id === omId);
  if (ownIndex === -1) throw new Error(`OM ${omId} not found among the members of its batch`);
  const {whoAndNow} = resolution;

  const outcome = await runInTransaction(async (transaction): Promise<BatchOutcome> => {
    const memberPath = (member: BatchMember) => docPath(member.config.firestoreCollection, member.light.id!);
    const fulls = await Promise.all(members.map((member) =>
      transaction.get(member.config.firestoreCollection, member.light.id!)));
    const oms = new Map<string, OMItem>();
    members.forEach((member, i) => {
      const full = fulls[i];
      if (!full) throw new Error(`OM document ${memberPath(member)} not found`);
      oms.set(memberPath(member), mergeFullDocument({...member.light, type: member.config.type}, full));
    });
    const omOf = (member: BatchMember) => oms.get(memberPath(member))!;
    const ownMember = members[ownIndex];
    const own = omOf(ownMember);
    // The merge itself approved a merged member and named its survivor
    if (resolution.kind !== 'merged') {
      if (own.status === 'deleted' || (resolution.kind === 'approve' && own.status === 'approved')) {
        return {kind: 'alreadyResolved', status: own.status};
      }
      if (own[ownMember.config.finalOmIdKey]) {
        return {kind: 'alreadyResolved', status: 'committed'};
      }
    }
    const wasApproved = resolution.kind !== 'merged' && own.status === 'approved';

    const updates = createUpdateQueue();
    if (resolution.kind === 'approve') {
      own.status = 'approved';
      updates.queue(ownMember.config.firestoreCollection, omId, {
        status: 'approved',
        approvedBy: whoAndNow,
        editedOn: whoAndNow,
        ...(resolution.linkedSalesforceId ? {linkedSalesforceId: resolution.linkedSalesforceId} : {}),
      });
      if (resolution.linkedSalesforceId) {
        own.linkedSalesforceId = resolution.linkedSalesforceId;
      }
    } else if (resolution.kind === 'delete') {
      own.status = 'deleted';
      updates.queue(ownMember.config.firestoreCollection, omId, {
        status: 'deleted',
        deleted: whoAndNow.timestamp,
        editedOn: whoAndNow,
      });
    }

    const nextForReview = nextUnresolvedSibling(
      members, ownIndex, (member) => omOf(member).status === 'approved' || omOf(member).status === 'deleted',
    );
    // Its Batch was Resolved but never Committed: committing the rest here could duplicate records already saved.
    if (wasApproved && !nextForReview) {
      throw new Error('This OM\'s batch never finished committing and needs a repair');
    }
    const approved = nextForReview ? [] : members.filter((member) => omOf(member).status === 'approved');
    // A merged member gets no record of its own: the batch links and releases the record it was merged into
    const survivorIds = approved.flatMap((member) => omOf(member).mergedInto?.id ?? []);
    const committing = approved.filter((member) => !omOf(member).mergedInto);
    if (committing.some((member) => omOf(member)[member.config.finalOmIdKey])) {
      throw new Error('Part of this OM\'s batch was committed earlier and needs a repair');
    }

    const linkEdits = await planLinkEdits(transaction, oms, [
      ...(resolution.kind === 'delete'
        ? [{source: own, edit: (links: LinkedDoc[]) => links.filter((link) => link.id !== omId)}]
        : []),
      ...committing.map(omOf).map((om, i) => ({
        source: om,
        edit: (links: LinkedDoc[]) => links.map((link) => link.id === om.id
          ? {id: committing[i].committedId, collection: link.collection.replace(/-om$/, '')}
          : link),
      })),
    ]);

    const committedLinks = (type: string) => committing
      .filter((member) => member.config.type === type)
      .map((member) => ({collection: member.config.finalFirestoreCollection, id: member.committedId}));
    const survivorLinks = survivorIds.map((id) => ({collection: 'beyondMaps-properties', id}));
    const committed = committing.map((member): CommittedMember => {
      const om = omOf(member);
      const siblingLinks = member.config.type === 'building'
        ? committedLinks('portfolio')
        : [...survivorLinks, ...committedLinks('building')];
      const writeable = buildCommittedRecord(om, member.committedId, siblingLinks, whoAndNow);
      return {om, config: member.config, writeable};
    });
    const releases = await planSurvivorReleases(transaction, survivorIds, committedLinks('portfolio'));

    for (const {om, config, writeable} of committed) {
      transaction.set(config.finalFirestoreCollection, writeable.id, writeable);
      updates.queue(config.firestoreCollection, om.id!, {[config.finalOmIdKey]: writeable.id, editedOn: whoAndNow});
    }
    for (const {collection, id, linkedDocuments} of linkEdits) {
      updates.queue(collection, id, {linkedDocuments, editedOn: whoAndNow});
    }
    for (const {id, update} of releases) {
      updates.queue('beyondMaps-properties', id, {...update, editedOn: whoAndNow});
    }
    updates.flush(transaction);

    if (nextForReview) {
      const next = {...nextForReview.light, type: nextForReview.config.type, status: omOf(nextForReview).status};
      return {kind: 'nextForReview', next, linkEdits};
    }
    return {kind: 'committed', committed, linkEdits, survivorIds, releases};
  });

  if (outcome.kind === 'alreadyResolved') {
    getStore().commit('setSnack', {message: `Already ${outcome.status}.`});
    return null;
  }
  // Closed only after the write, so a failed Commit leaves the reviewer's window open. A verdict that opens nothing
  // next (a standalone delete) closes only its own tab.
  if (outcome.kind === 'committed' && !outcome.committed.length) {
    getStore().dispatch('infowindow/closeInfoWindowTab', omId);
  } else {
    getStore().dispatch('infowindow/closeInfoWindow');
  }
  const awaitDocs = applyLinkEditsLocally(outcome.linkEdits, whoAndNow);
  if (outcome.kind === 'nextForReview') {
    await placeMarkerForOM(outcome.next);
    return {awaitDocs, opened: true};
  }
  const survivors = outcome.committed.some((member) => member.config.type === 'portfolio')
    ? (await Promise.all(outcome.survivorIds.map(survivorSnapshot))).filter((snapshot) => snapshot !== null)
    : [];
  // Outside the transaction: a portfolio scenario nests every building and can exceed Firestore's 1 MiB doc limit.
  for (const input of baseScenarioInputs(outcome.committed, survivors)) {
    await safeWriteBaseScenario(input);
  }
  return {
    awaitDocs: [
      ...awaitDocs,
      ...showCommittedMembers(outcome.committed, whoAndNow),
      ...showReleasedSurvivors(outcome.releases, whoAndNow),
    ],
    opened: outcome.committed.length > 0,
  };
};

const applyLinkEditsLocally = (linkEdits: LinkEdit[], whoAndNow: UserAction): AwaitDoc[] => {
  const awaitDocs: AwaitDoc[] = [];
  for (const {collection, id, linkedDocuments} of linkEdits) {
    let collectionItem;
    try {
      collectionItem = getItemFromCollection(collection, id);
    } catch {
      continue;
    }
    const itemToUpdate = collectionItem.value!;
    itemToUpdate.linkedDocuments = linkedDocuments;
    itemToUpdate.editedOn = whoAndNow;
    if (collectionItem.collectionType === 'filter') {
      getCollection(collectionItem.collectionName!)?.addItems(itemToUpdate);
      awaitDocs.push({collection: collectionItem.collectionName!, id, editedAtMillis: whoAndNow.timestamp.getTime()});
    }
  }
  return awaitDocs;
};

const showCommittedMembers = (committed: CommittedMember[], whoAndNow: UserAction): AwaitDoc[] => {
  const iwActions: Array<() => void> = [];
  const awaitDocs: AwaitDoc[] = [];
  const buildingsFirst = [
    ...committed.filter((member) => member.config.type === 'building'),
    ...committed.filter((member) => member.config.type === 'portfolio'),
  ];
  for (const {om, config, writeable} of buildingsFirst) {
    // Not awaited: copying the OM's edit history must never block or fail a Commit.
    callProcedure('firestore_export', 'copyOmRows', [
      config.bigQuerySourceTable,
      config.bigQueryDestTable,
      om.id!,
      writeable.id,
    ]).catch((error: unknown) => console.error('Failed to copy OM audit rows:', error));
    awaitDocs.push({
      collection: collectionNameByFirestoreCollection[config.finalFirestoreCollection],
      id: writeable.id,
      editedAtMillis: whoAndNow.timestamp.getTime(),
    });

    if (config.type === 'building') {
      const comps = formatOmComps(om, writeable);
      const body = {...writeable, markerData: createMarkerData(om, writeable.id), comps};
      // Local-only: these aggregated fields (allowEdit:false) are stripped when saving to Firestore.
      addAggregatedFieldsToBody(body);
      getCollection('competitiveBuildings').addItems(body);
      getCollection('competitiveBuildingComps').addItems(...(comps ?? []));
      iwActions.push(() => getStore().dispatch('infowindow/createCompetitiveBuildingTab', {
        subject: {...body, firestoreCollection: 'beyondMaps-properties', filterCollection: 'competitiveBuildings'},
      }));
    } else {
      const body = {...writeable, markerData: createMarkerData(om, writeable.id)};
      getCollection('competitivePortfolios').addItems(body);
      iwActions.push(() => getStore().dispatch('infowindow/createCompetitivePortfolioTab', {
        subject: {...body, firestoreCollection: 'beyondMaps-portfolios', filterCollection: 'competitivePortfolios'},
      }));
    }
  }
  iwActions[0]?.();
  return awaitDocs;
};

/**
 * Sets the OM to an `approved` status and determines if it should be committed to the database.
 * If the OM is a part of a portfolio, it will not be committed until the entire portfolio is approved.
 * @param omId - The ID of the OM to approve.
 */
const approveOM = async (config: OmConfigItem, omId: string) => {
  if (!config) throw new Error('config was not provided for function that approves an OM');
  if (!omId) throw new Error('omId was not provided for function that approves an OM');

  const om = await getDocument(config.firestoreCollection, omId);
  if (!om) throw new Error(`om with id '${omId}' not found in collection ${config.firestoreCollection}`);
  // Before Salesforce: approving a deleted OM would create a Deal nothing links to.
  if (om.status === 'approved' || om.status === 'deleted') {
    getStore().commit('setSnack', {message: `Already ${om.status}.`});
    return;
  }
  // After the check, so a resolved OM never takes a late edit.
  await flushOpenReviewEdits(omId);
  om.type = config.type;
  const whoAndNow = currentUserAction({requireDisplayName: true});

  // Salesforce first, so the approval and its id land in one write; a Batch's buildings are covered by its portfolio.
  const salesforce = config.type === 'portfolio' || !om.omBatchId
    ? await createSalesforceRecordIfEnabled(
      config.firestoreCollection as 'beyondMaps-properties-om' | 'beyondMaps-portfolios-om', omId,
    )
    : {salesforceId: null, failed: false};
  const members = om.omBatchId ? batchMembers(om.omBatchId) : [standaloneMember(config, omId, om)];
  const outcome = await resolveOmItem(
    omId, members, {kind: 'approve', whoAndNow, linkedSalesforceId: salesforce.salesforceId},
  );
  if (!outcome) return;
  if (salesforce.failed) {
    // Not "couldn't be created": after a timeout the function may still create the record.
    getStore().commit('setSnack', {
      message: 'Approved, but it couldn\'t be linked to Salesforce. Please contact solutions-engineering@northpointkc.com',
      color: 'warning',
      duration: -1,
    });
  }
  refreshBackendFiltersForDocs(outcome.awaitDocs);
};

/** Moves an OM batch on once a member was merged into an existing record, as approving it would. */
const advanceOmBatch = async (om: LightOMItem): Promise<boolean> => {
  if (!om.omBatchId) throw new Error('To move a batch on, the merged OM must have a batch ID.');
  const whoAndNow = currentUserAction({requireDisplayName: true});
  const outcome = await resolveOmItem(om.id!, batchMembers(om.omBatchId), {kind: 'merged', whoAndNow});
  if (!outcome) return false;
  refreshBackendFiltersForDocs(outcome.awaitDocs);
  return outcome.opened;
};

/**
 * Mark an OM record as deleted. If no other OM records share the uploaded PDF, the underlying document metadata
 * will be cleaned up to allow the same PDF to be re-uploaded in the future. This may involve deleting the
 * document metadata record or updating its status.
 */
const handleDeleteOm = async (config: OmConfigItem, omId: string) => {
  if (!config) throw new Error('config was not provided for function that approves an OM');
  if (!omId) throw new Error('omId was not provided for function that approves an OM');
  const om = await getDocument(config.firestoreCollection, omId);
  if (!om) throw new Error(`om with id '${omId}' not found in collection ${config.firestoreCollection}`);
  om.type = config.type;

  const whoAndNow = currentUserAction({requireDisplayName: true});
  const members = om.omBatchId ? batchMembers(om.omBatchId) : [standaloneMember(config, omId, om)];
  const awaitDocs = (await resolveOmItem(omId, members, {kind: 'delete', whoAndNow}))?.awaitDocs ?? [];

  // Check if other active OMs are using the same uploaded document.
  // The current OM is already marked as 'deleted'.
  const buildingCount = await countFromCollection('beyondMaps-properties-om', {
    where: [
      {field: 'uploadedDocId', opStr: '==', value: om.uploadedDocId},
      {field: 'status', opStr: 'in', value: ['review', 'generating', 'approved']},
    ],
  });
  const portfolioCount = await countFromCollection('beyondMaps-portfolios-om', {
    where: [
      {field: 'uploadedDocId', opStr: '==', value: om.uploadedDocId},
      {field: 'status', opStr: 'in', value: ['review', 'generating', 'approved']},
    ],
  });

  // If count is 0, no other OMs actively use this document.
  if (buildingCount + portfolioCount === 0) {
    await updateDocument('upload_document', om.uploadedDocId, {status: 'deleted'});
  }
  // If count > 0, other OMs still use this document; do not modify 'upload_document' status.

  refreshBackendFiltersForDocs(awaitDocs);
};

/**
 * Creates an OM object in the `beyondMaps-properties-om` collection.
 */
const createOM = async (doc: {docName: string; documentId: string}) => {
  const generatedBy = {
    name: currentUser()?.displayName || '',
    uid: currentUser()?.uid || '',
    timestamp: new Date(),
  };
  const om: OMItem = {
    status: 'generating',
    name: doc.docName,
    generatedBy,
    uploadedDocId: doc.documentId,
    approvedBy: null,
    originalFields: null,
    createdCompetitiveBuildingId: null,
    createdCompetitivePortfolioId: null,
    // fields related to the competitive building
    id: null,
    createdOn: null,
    // The Recency Listener only sees docs whose editedOn advances.
    editedOn: generatedBy,
    deleted: null,
    displayFields: null,
    geohash: null,
    location: null,
    comps: null,
    underwritingData: {},
  };
  return await addDocument('beyondMaps-properties-om', om);
};

/** Reveals the competitive map layer for a staged record's kind (OM + bulk share this). */
const revealCompetitiveSection = (kind: 'building' | 'portfolio') => {
  if (kind === 'building') {
    if (getStore().state.compViewMode !== 'buildings') {
      getStore().dispatch('filters/setCompViewMode', 'buildings');
    }
    getStore().dispatch('filters/setSectionChecked', {market: 'Industrial', section: 'Competitive Buildings', checked: true});
  } else {
    getStore().dispatch('filters/setSectionChecked', {market: 'Industrial', section: 'Competitive Portfolios', checked: true});
  }
};

/**
 * Places (or re-shows) a staged in-review marker, pans to it, and highlights it. Shared by OM
 * and bulk; `events` carries the OM drag handler (bulk passes none).
 */
const placeStagingMarker = async (
  localCollection: string,
  marker: {
    type: string;
    id: string;
    name: string;
    location: {latitude: number; longitude: number};
    icon: string;
    events?: unknown[];
  },
) => {
  if (Markers.exists(localCollection, marker.id)) {
    Markers.setVisible(localCollection, [marker.id], true);
  } else {
    Markers.addMarkers(localCollection, [{
      collection: localCollection,
      type: marker.type,
      id: marker.id,
      name: marker.name,
      latLng: {lat: marker.location.latitude, lng: marker.location.longitude},
      icon: marker.icon,
      events: marker.events ?? [],
      visible: true,
      draggable: false,
      clickable: false,
    }]);
  }
  await MapService.centerViewport({lat: marker.location.latitude, lng: marker.location.longitude}, 15);
  Markers.setHighlight(localCollection, marker.id, true);
};

const placeMarkerForOM = async (om: LightOMItem) => {
  if (!om) {
    throw new Error('OM not found');
  }

  const config = omConfig[om.type!];

  // For an approved OM, the committed record in the final ("original") collection is the
  // source of truth — both for the marker location and for the read-only info window we open
  // (mirroring what clicking the committed marker on the map does). Fetch it once here.
  // If anything is missing we fall through to the existing `!om.location` fallback below and
  // the OM info window further down.
  let committedDoc: Record<string, unknown> | undefined;
  if (om.status === 'approved') {
    const finalId = om[config.finalOmIdKey];
    if (finalId) {
      committedDoc = await getDocument(config.finalFirestoreCollection, finalId, true);
      const loc = committedDoc?.location as {latitude: number; longitude: number} | undefined;
      if (loc && typeof loc.latitude === 'number' && typeof loc.longitude === 'number') {
        om.location = {latitude: loc.latitude, longitude: loc.longitude};
      }
    }
  }

  if (!om.location) {
    getStore().commit('setSnack', {message: 'No location was found for the OM.'});
    // Default location to the KC office
    om.location = {latitude: 39.155630292693225, longitude: -94.57613179930004};
  }

  if (om.type !== 'building' && om.type !== 'portfolio') {
    getStore().commit('setSnack', {message: 'Unknown OM type.'});
    return;
  }
  revealCompetitiveSection(om.type);

  await createMarkerForOm(om);

  if (om.status === 'approved' && committedDoc) {
    // Open the same read-only competitive building/portfolio info window the committed map
    // marker opens, rather than the editable OM tab. The marker we just placed (in
    // `config.localCollection`, keyed by the OM id) is what the info window highlights.
    getStore().dispatch(`infowindow/${config.finalInfoWindowAction}`, {
      subject: {
        ...committedDoc,
        firestoreCollection: config.finalFirestoreCollection,
        filterCollection: collectionNameByFirestoreCollection[config.finalFirestoreCollection],
        markerData: {
          collection: config.localCollection,
          id: om.id,
          icon: config.markerIcon,
        },
      },
    });
  } else {
    getStore().dispatch('infowindow/createCompetitiveOmTab', {subject: await hydrateOMItem(om)});
  }
};

/**
 * The one place a Light OM Item widens to the full document. The light copy's `type` (client-side)
 * and `location` (possibly defaulted by the caller) win over the document's.
 */
const mergeFullDocument = (om: LightOMItem, full: Record<string, unknown>): OMItem =>
  ({...om, ...full, type: om.type, location: om.location} as OMItem);

const hydrateOMItem = async (om: LightOMItem): Promise<OMItem> => {
  const config = omConfig[om.type!];
  const full = await getDocument(config.firestoreCollection, om.id!, true);
  if (!full) {
    throw new Error(`OM document ${config.firestoreCollection}/${om.id} not found`);
  }
  return mergeFullDocument(om, full);
};

/**
 * Creates a marker for the OM competitive building on the map.
 * @param om - the OM to create a marker for.
 * @returns a function to delete the marker.
 */
const createMarkerForOm = async (om: LightOMItem) => {
  try {
    const config = omConfig[om.type!];
    const dragend = {
      event: 'dragend',
      callback: async (e: {lat: number; lng: number}) => {
        om.location = {latitude: e.lat, longitude: e.lng};
        om.editedOn = currentUserAction();
        await updateDocument(config.firestoreCollection, om.id!, {location: om.location, editedOn: om.editedOn});
      },
    };
    await placeStagingMarker(config.localCollection, {
      type: config.fullType,
      id: om.id!,
      name: om.name,
      location: om.location!,
      icon: config.markerIcon,
      events: [dragend],
    });
  } catch (error) {
    throw wrapError(error, `Error creating marker for om ${om.type}`);
  }
};

/** Staged-record store arrays, keyed by their `-om`/`-bulk` Firestore collection. */
const STAGING_STORE_COLLECTION: Record<string, string> = {
  'beyondMaps-portfolios-om': 'omCompetitivePortfolios',
  'beyondMaps-properties-om': 'omCompetitiveBuildings',
  'beyondMaps-portfolios-bulk': 'bulkCompetitivePortfolios',
  'beyondMaps-properties-bulk': 'bulkCompetitiveBuildings',
};

const getItemFromCollection = (collection: string, id: string) => {
  try {
    const result: {
      value?: LightOMItem & {
        collection: string;
        deleted: null | Date;
        id: string;
        name: string;
        parentSection?: string;
        linkedDocuments: LinkedDoc[];
      };
      collectionType?: string;
      collectionName?: string;
    } = {};
    // Staged records (OM + bulk) live in store arrays keyed by their `-om`/`-bulk` collection;
    // everything else resolves from a filter collection.
    const stagingStore = STAGING_STORE_COLLECTION[collection];
    if (stagingStore) {
      result.value = getStore().state[stagingStore].find((item: {id: string}) => item.id === id);
      if (!result.value) throw new Error(`No item found in store collection '${stagingStore}' for '${id}'`);
      result.collectionType = 'storeOm';
      result.collectionName = stagingStore;
    } else {
      const collectionName = collectionNameByFirestoreCollection[collection];
      if (!collectionName) throw new Error(`No mapped filter collection name found for '${collection}'`);
      const filterCollection = getCollection(collectionName);
      if (!filterCollection) throw new Error(`No filter collection found for '${collection}'`);
      result.value = filterCollection?.getItem(id);
      if (!result.value) throw new Error(`No item found in filter collection '${collectionName}' for '${id}'`);
      result.collectionType = 'filter';
      result.collectionName = collectionName;
    }
    return result;
  } catch (error) {
    throw wrapError(error, `Error getting id '${id}' from collection ${collection}`);
  }
};

export {
  omConfig,
  uploadDocument,
  createOM,
  getReadyForReviewOMs,
  placeMarkerForOM,
  handleDeleteOm,
  deleteOmUpload,
  approveOM,
  advanceOmBatch,
  getItemFromCollection,
  SALESFORCE_WAIT_MS,
  // Reused by BulkStagingService (BMAPS-3518) — all config-free / pure:
  buildCommittedCompetitiveBody,
  buildCommittedMarkerData,
  formatOmComps,
  addAggregatedFieldsToBody,
  nextUnresolvedSibling,
  revealCompetitiveSection,
  placeStagingMarker,
  planSurvivorReleases,
  showReleasedSurvivors,
  refreshBackendFiltersForDocs,
};
